/**
 * Pure geometry for the assistant launcher's corner-pinning behavior. No
 * React, no DOM globals — safe to unit test and safe to call from an
 * animation frame callback.
 */

export type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export const CORNERS: readonly Corner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

export type Point = { x: number; y: number };
type Viewport = { width: number; height: number };

/** Inset from the viewport edge the puck rests at when pinned to a corner. */
export const EDGE_MARGIN = 24;
/** Seconds of release velocity projected forward to find the landing corner. */
const PROJECTION_S = 0.12;
/** Puck diameter in px. */
export const PUCK_SIZE = 56;
/** Visual gap (px) between the puck and an action bar kept clear of it. */
const PUCK_GAP = 8;
/**
 * Horizontal room (px) a bottom-right footer action bar must leave clear so the
 * pinned puck never sits over its buttons: the puck's width, its edge margin,
 * and a small gap. Reserved unconditionally, whichever corner the puck is
 * pinned to: tracking the corner would couple every footer to assistant state
 * for a one-handful-of-px saving.
 */
export const PUCK_FOOTER_CLEARANCE = PUCK_SIZE + EDGE_MARGIN + PUCK_GAP;

/** Top-left px position of a `size`-square puck pinned to `corner`. */
export function anchorFor(corner: Corner, viewport: Viewport, size: number): Point {
  const x = corner === 'top-left' || corner === 'bottom-left'
    ? EDGE_MARGIN
    : viewport.width - size - EDGE_MARGIN;
  const y = corner === 'top-left' || corner === 'top-right'
    ? EDGE_MARGIN
    : viewport.height - size - EDGE_MARGIN;
  return { x, y };
}

/** pos + velocity * PROJECTION_S — the "momentum" part of the snap. */
export function projectRelease(pos: Point, velocity: Point): Point {
  return {
    x: pos.x + velocity.x * PROJECTION_S,
    y: pos.y + velocity.y * PROJECTION_S,
  };
}

/** Corner whose anchor is nearest `point`. */
export function nearestCorner(point: Point, viewport: Viewport, size: number): Corner {
  let best: Corner = CORNERS[0];
  let bestDistance = Infinity;
  for (const corner of CORNERS) {
    const anchor = anchorFor(corner, viewport, size);
    const dx = anchor.x - point.x;
    const dy = anchor.y - point.y;
    const distance = dx * dx + dy * dy;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = corner;
    }
  }
  return best;
}

export type Rect = { top: number; left: number; width: number; height: number };

/**
 * A `size`-square rect centered on `rect` — the shared geometry between the
 * launcher puck's flight target and the takeover surface's starting/ending
 * circle (`AssistantHost.tsx`'s captive-takeover choreography). Deriving both
 * from the same function is what keeps the puck and the circle it "becomes"
 * pixel-exact: the puck's top-left (`anchorFor`-shaped) is this rect's
 * `{left, top}`.
 */
export function centerCircleRect(rect: Rect, size: number): Rect {
  return {
    top: rect.top + rect.height / 2 - size / 2,
    left: rect.left + rect.width / 2 - size / 2,
    width: size,
    height: size,
  };
}

/** Magnitude of the disc "pop" on takeoff/return — mirrors the open/dismiss pulse. */
export const TAKEOVER_PULSE_MAGNITUDE = 60;
